/**
 * UserTypeSelector - Individual vs Company selection
 */

import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import type { UserType } from '@/types/auth';

interface UserTypeSelectorProps {
  value: UserType;
  onChange: (type: UserType) => void;
}

const userTypes: { type: UserType; icon: ReactNode; title: string; description: string }[] = [
  {
    type: 'individual',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    title: 'Individual',
    description: 'Personal use for health & fitness',
  },
  {
    type: 'company',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    title: 'Company',
    description: 'Business use with team features',
  },
];

export function UserTypeSelector({ value, onChange }: UserTypeSelectorProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {userTypes.map(({ type, icon, title, description }) => {
        const isSelected = value === type;
        return (
          <motion.button
            key={type}
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onChange(type)}
            className={cn(
              'relative p-4 rounded-xl border-2 text-left transition-all duration-200',
              isSelected
                ? 'border-hud-primary bg-hud-primary/10 shadow-lg shadow-hud-primary/20'
                : 'border-glass-border bg-glass-bg hover:border-text-muted'
            )}
          >
            {/* Selection indicator */}
            {isSelected && (
              <motion.div
                layoutId="userTypeIndicator"
                className="absolute top-2 right-2 w-5 h-5 rounded-full bg-hud-primary flex items-center justify-center"
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
            )}

            <div className={cn(
              'mb-3 transition-colors',
              isSelected ? 'text-hud-primary' : 'text-text-secondary'
            )}>
              {icon}
            </div>

            <h3 className={cn(
              'font-semibold mb-1 transition-colors',
              isSelected ? 'text-white' : 'text-text-primary'
            )}>
              {title}
            </h3>

            <p className="text-xs text-text-secondary">
              {description}
            </p>
          </motion.button>
        );
      })}
    </div>
  );
}
